// Game-side audio orchestrator. Owns the AudioContext, instantiates
// EngineSynth + Sfx, routes them through engine/sfx sub-buses + a master
// gain + compressor, persists user volume / mute prefs to localStorage.
//
// One instance per game session, constructed up-front but inert until
// start() is called — start() must run on a user gesture (browser
// autoplay policy).

import { EngineSynth, type PhysicsSignals } from './EngineSynth';
import { loadAudioDefaults } from './AudioDefaults';
import { Sfx } from './Sfx';

const PREFS_KEY = 'tst:audio-prefs';
export const AUDIO_PREFS_VERSION = 2;

interface AudioPrefs {
  version: number;
  muted: boolean;
  master: number;   // 0–1
  engine: number;   // 0–1
  sfx: number;      // 0–1
}

function defaultPrefs(): AudioPrefs {
  const mix = loadAudioDefaults().mix;
  return {
    version: AUDIO_PREFS_VERSION,
    muted: false,
    master: clamp01(mix.master),
    engine: clamp01(mix.engine),
    sfx: clamp01(mix.sfx),
  };
}

function readPrefs(): AudioPrefs {
  const defaults = defaultPrefs();
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as Partial<AudioPrefs>;
    if (parsed.version !== AUDIO_PREFS_VERSION) return defaults;
    return {
      version: AUDIO_PREFS_VERSION,
      muted: typeof parsed.muted === 'boolean' ? parsed.muted : defaults.muted,
      master: clamp01(parsed.master ?? defaults.master),
      engine: clamp01(parsed.engine ?? defaults.engine),
      sfx: clamp01(parsed.sfx ?? defaults.sfx),
    };
  } catch {
    return defaults;
  }
}

function writePrefs(prefs: AudioPrefs): void {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch { /* ignore — storage may be unavailable */ }
}

function clamp01(x: number): number { return Math.max(0, Math.min(1, x)); }

export class GameAudio {
  readonly engine = new EngineSynth();
  private sfx: Sfx | null = null;
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private engineBus: GainNode | null = null;
  private sfxBus: GainNode | null = null;
  private comp: DynamicsCompressorNode | null = null;
  private prefs: AudioPrefs = readPrefs();
  private started = false;
  private startPromise: Promise<void> | null = null;

  // Auto-backfire detection state.
  private lastThrottle = 0;
  private lastBoost = 0;
  private lastBackfireMs = 0;
  private lastBoostMs = 0;

  // Async-starts the audio context, loads worklets, wires routing. Idempotent.
  // Must be called from a user gesture (e.g. clicking Race) for browser
  // autoplay policy. Returns the in-flight promise on subsequent calls.
  async start(): Promise<void> {
    if (this.started) return;
    if (this.startPromise) return this.startPromise;

    this.startPromise = (async () => {
      const Ctor = window.AudioContext
        ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new Ctor();

      this.masterGain = this.ctx.createGain();
      this.engineBus = this.ctx.createGain();
      this.sfxBus = this.ctx.createGain();
      this.comp = this.ctx.createDynamicsCompressor();
      this.comp.threshold.value = -6;
      this.comp.knee.value = 12;
      this.comp.ratio.value = 2.5;
      this.comp.attack.value = 0.008;
      this.comp.release.value = 0.18;

      this.engineBus.connect(this.masterGain);
      this.sfxBus.connect(this.masterGain);
      this.masterGain.connect(this.comp).connect(this.ctx.destination);

      const defaults = loadAudioDefaults();
      await this.engine.start({ context: this.ctx, output: this.engineBus });
      this.engine.applyPreset(defaults.enginePreset);
      this.sfx = new Sfx(this.ctx, this.sfxBus, defaults.sfx);
      await this.sfx.prepareScrape();

      this.applyPrefs();
      this.started = true;
    })().catch((err) => {
      console.warn('[twin-suns-turbo] audio start failed', err);
      this.startPromise = null;
      throw err;
    });

    return this.startPromise;
  }

  isStarted(): boolean { return this.started; }

  // ---- prefs / volume / mute ------------------------------------------

  isMuted(): boolean { return this.prefs.muted; }
  toggleMuted(): boolean {
    this.setMuted(!this.prefs.muted);
    return this.prefs.muted;
  }
  setMuted(muted: boolean): void {
    this.prefs.muted = muted;
    this.applyPrefs();
    writePrefs(this.prefs);
  }

  getMasterVolume(): number { return this.prefs.master; }
  setMasterVolume(v: number): void {
    this.prefs.master = clamp01(v);
    this.applyPrefs();
    writePrefs(this.prefs);
  }

  getEngineVolume(): number { return this.prefs.engine; }
  setEngineVolume(v: number): void {
    this.prefs.engine = clamp01(v);
    this.applyPrefs();
    writePrefs(this.prefs);
  }

  getSfxVolume(): number { return this.prefs.sfx; }
  setSfxVolume(v: number): void {
    this.prefs.sfx = clamp01(v);
    this.applyPrefs();
    writePrefs(this.prefs);
  }

  private applyPrefs(): void {
    if (!this.masterGain || !this.engineBus || !this.sfxBus) return;
    this.masterGain.gain.value = this.prefs.muted ? 0 : this.prefs.master;
    this.engineBus.gain.value = this.prefs.engine;
    this.sfxBus.gain.value = this.prefs.sfx;
  }

  // ---- per-frame physics → audio --------------------------------------

  update(signals: PhysicsSignals, dt: number): void {
    if (!this.started) return;
    this.engine.update(signals, dt);

    // Auto-backfire on hard throttle release at speed.
    const throttleVel = (signals.throttle - this.lastThrottle) / Math.max(1e-3, dt);
    this.lastThrottle = signals.throttle;
    if (throttleVel < -2.5 && signals.speed > 0.4 && this.sfx) {
      const now = performance.now();
      if (now - this.lastBackfireMs > 250) {
        this.sfx.backfire(Math.min(1, signals.speed));
        this.lastBackfireMs = now;
      }
    }

    const boost = signals.boost ?? 0;
    const boostCharge = signals.boostCharge ?? 1;
    if (boost > 0.01) {
      this.sfx?.setBoost(Math.max(0.55, Math.min(1, boost)));
    } else if (this.lastBoost > 0.01 && boostCharge <= 0.001) {
      this.sfx?.stopBoostFast();
    } else {
      this.sfx?.stopBoost();
    }
    if (boost > 0.5 && this.lastBoost <= 0.5 && this.sfx) {
      const now = performance.now();
      if (now - this.lastBoostMs > 180) {
        this.sfx.backfire(Math.max(0.75, Math.min(1, signals.speed + 0.2)));
        this.lastBoostMs = now;
      }
    }
    this.lastBoost = boost;

    const airBrake = signals.airBrake ?? signals.brake;
    if (airBrake > 0.02) this.sfx?.setAirBrake(airBrake, signals.speed);
    else this.sfx?.stopAirBrake();

    if (signals.slip > 0.02) this.sfx?.setSlipRasp(signals.slip, signals.speed);
    else this.sfx?.stopSlipRasp();
  }

  // ---- explicit triggers ----------------------------------------------

  playIgnition(): number {
    const duration = this.sfx?.ignite() ?? 0;
    this.engine.update({ throttle: 0, brake: 0, speed: 0, slip: 0, load: 0, onTrack: true }, 1 / 60);
    return duration;
  }

  notifyCollision(impactSpeed: number): void {
    if (!this.sfx) return;
    const intensity = Math.pow(clamp01((impactSpeed - 4) / 28), 0.85);
    this.sfx.wallThunk(intensity);
  }

  setScrape(intensity: number, speed01: number, roughness = 0.5, surface?: 'track' | 'dirt' | 'wall'): void {
    this.sfx?.setScrape(intensity, speed01, roughness, surface);
  }
  stopScrape(): void { this.sfx?.stopScrape(); }

  silenceEngine(): void {
    this.stopScrape();
    this.sfx?.stopBoost();
    this.sfx?.stopSlipRasp();
    this.sfx?.stopAirBrake();
    this.sfx?.stopIgnition();
    this.lastThrottle = 0;
    this.lastBoost = 0;
    this.engine.silence();
  }

  airBrake(intensity = 1): void { this.sfx?.setAirBrake(intensity); }
  stopAirBrake(): void { this.sfx?.stopAirBrake(); }
  kerbTick(speed01: number): void { this.sfx?.kerbTick(speed01); }

  // ---- lifecycle ------------------------------------------------------

  async suspend(): Promise<void> {
    if (this.started) await this.engine.suspend();
  }
  async resume(): Promise<void> {
    if (this.started) await this.engine.resume();
  }
}

// Module-level singleton — mirrors the titleThemeMusic export pattern so
// any file can import { gameAudio } and reach the same instance.
export const gameAudio = new GameAudio();
